import {AGENCY_KEYS} from "@/api/agencyApi";
import Button from "@/components/Common/Button";
import FileUpload from "@/components/Common/FileUpload";
import AddressForm from "@/components/Common/forms/AddressForm";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import InputField from "@/components/Common/forms/InputField";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useSettingsDirty} from "@/hooks/useSettingsNavigation";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {useSettingsAgencyStore} from "@/store/agency";
import type {TSettingsScope} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const AgencyInfoPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["agency_profile", "common", "multi_account", "settings"]);
  const isAdmin = useIsAdmin();
  const agency = useSettingsAgencyStore(state => state.agency);
  const errors = useSettingsAgencyStore(state => state.errors);
  const setter = useSettingsAgencyStore(state => state.setter);
  const validateAgencyInfo = useSettingsAgencyStore(state => state.agencyInfoValidation);
  const {refetch: refetchUser} = useFetchUser();
  useSettingsDirty(useSettingsAgencyStore(state => state.hasUnsavedChanges()));

  const {mutateAsync: updateAgency, isPending: isSaving} = useMutation({
    mutationKey: AGENCY_KEYS.mAgency(scope.id),
    mutationFn: () =>
      client.patch(AGENCY_KEYS.mAgency(scope.id)[0], {
        address: agency.address,
        city: agency.city,
        country: agency.country,
        description: agency.description,
        name: agency.name,
        place_description: agency.place_description,
        place_id: agency.place_id,
        postal_code: agency.postal_code,
        website: agency.website,
      }),
  });
  const {mutate: uploadPicture, isPending: isUploading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgency(scope.id),
    mutationFn: ({file}: {file: File}) => {
      const formData = new FormData();

      formData.append("picture", file);
      formData.append("picture320", file);
      formData.append("picture640", file);

      return client.patch(AGENCY_KEYS.mAgency(scope.id)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgency(scope.id)});
      refetchUser();
    },
  });

  const isLargeBrand = agency.large_brand;

  const save = async () => {
    if (!validateAgencyInfo() && !isAdmin) return;

    await updateAgency();

    queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgency(scope.id)});
    refetchUser();
  };

  if (agency.id !== scope.id) {
    return (
      <div className="flex flex-col gap-8">
        <SkeletonLoader className="h-4.5 max-w-40" />
        <SkeletonLoader className="size-44 self-center rounded-full" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-5 max-w-32" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-5 max-w-32" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-5 max-w-28" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-5 max-w-24" />
            <SkeletonLoader className="h-21 rounded-lg" />
          </div>
        </div>
        <SkeletonLoader className="h-10 rounded-lg sm:max-w-32" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">
        {t(isLargeBrand ? "multi_account:large_brand_info" : "agency_profile:agency_info")}
      </span>
      <div className="flex flex-col items-center gap-3">
        <FileUpload file={agency.picture} isLoading={isUploading} onUpload={files => uploadPicture({file: files[0]})} />
      </div>
      <Can I="update" a="CompanyDetails" passThrough>
        {allowed => (
          <div className="flex flex-col gap-4">
            <InputField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
              error={errors?.name}
              isDisabled={!allowed}
              label={t(isLargeBrand ? "multi_account:large_brand_name" : "agency_profile:agency_name")}
              labelClassName="text-neutral-black text-sm font-medium"
              onBlur={() => validateAgencyInfo()}
              onChange={value =>
                setter(state => {
                  state.agency.name = value;
                })
              }
              placeholder={t(
                isLargeBrand ? "multi_account:large_brand_name_placeholder" : "agency_profile:agency_name_placeholder",
              )}
              value={agency.name ?? ""}
            />
            <AddressForm
              cityKey="city"
              className="gap-1"
              countryKey="country"
              errors={errors ?? undefined}
              inputClassName="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm bg-white"
              isDisabled={!allowed}
              key={agency.id}
              labelClassName="text-neutral-black text-sm font-medium"
              placeDescriptionKey="place_description"
              placeIdKey="place_id"
              postalClassName="sm:w-[calc(50%-4px)]"
              postalKey="postal_code"
              storeForm={agency}
              storeFormKey="agency"
              storeSetterFn={setter}
              streetKey="address"
              title={t(isLargeBrand ? "multi_account:large_brand_address" : "agency_profile:agency_address")}
              titleClassName="text-neutral-black text-sm font-medium"
              validateForm={validateAgencyInfo}
            />
            <InputField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
              error={errors?.website}
              isDisabled={!allowed}
              label={t(isLargeBrand ? "multi_account:large_brand_website" : "agency_profile:agency_web")}
              labelClassName="text-neutral-black text-sm font-medium"
              onBlur={() => validateAgencyInfo()}
              onChange={value =>
                setter(state => {
                  state.agency.website = value;
                })
              }
              placeholder={t(
                isLargeBrand ? "multi_account:large_brand_placeholder" : "agency_profile:agency_web_placeholder",
              )}
              value={agency.website ?? ""}
            />
            <AutosizeTextarea
              className="border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm"
              disabled={!allowed}
              htmlFor="agencyBio"
              id="agencyBio"
              maxRows={6}
              minRows={3}
              onChange={({target: {value}}) =>
                setter(state => {
                  state.agency.description = value;
                })
              }
              placeholder={t(
                isLargeBrand ? "multi_account:large_brand_bio_placeholder" : "agency_profile:agency_bio_placeholder",
              )}
              title={t(isLargeBrand ? "multi_account:large_brand_bio" : "agency_profile:agency_bio")}
              titleClassName="text-neutral-black text-sm font-medium"
              value={agency.description ?? ""}
            />
          </div>
        )}
      </Can>
      <Can I="update" a="CompanyDetails">
        <Button
          buttonStyle="Gradient"
          className="sticky bottom-0 h-10 w-full px-5 sm:static sm:w-fit"
          isDisabled={isSaving || (!!errors && Object.keys(errors).length > 0 && !isAdmin)}
          isLoading={isSaving}
          onClick={save}>
          {t("common:saveChanges")}
        </Button>
      </Can>
    </div>
  );
};

export default AgencyInfoPane;
